import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button } from '../components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const PaginationButton = styled(Button)`
  min-width: 40px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  margin: 0 ${props => props.theme.spacing.md};
`;

/**
 * Reusable pagination controls.
 *
 * @param {object}   props
 * @param {number}   props.currentPage
 * @param {number}   props.totalPages
 * @param {number}   props.totalCount
 * @param {number}   props.startIndex   - 0-based start index of current page
 * @param {number}   props.endIndex     - exclusive end index
 * @param {function} props.onPageChange - called with page number
 * @param {string}   [props.itemLabel='items'] - e.g. "forms", "users"
 */
export const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  startIndex,
  endIndex,
  onPageChange,
  itemLabel = 'items',
}) => {
  const theme = useTheme();

  if (totalPages <= 1) return null;

  // Build page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <PaginationContainer theme={theme}>
      <PaginationButton
        variant="secondary"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </PaginationButton>

      {getPageNumbers().map((page, idx) =>
        page === '...' ? (
          <span key={`ellipsis-${idx}`} style={{ color: theme.colors.muted }}>
            ...
          </span>
        ) : (
          <PaginationButton
            key={page}
            variant={currentPage === page ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => onPageChange(page)}
          >
            {page}
          </PaginationButton>
        )
      )}

      <PaginationButton
        variant="secondary"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </PaginationButton>

      <PageInfo theme={theme} type="small">
        Showing {startIndex + 1}-{Math.min(endIndex, totalCount)} of {totalCount} {itemLabel}
      </PageInfo>
    </PaginationContainer>
  );
};
